/**
 * SCRUM-241 — Structura Picking: recibir mercadería.
 *
 * Dos modos en la misma pantalla, porque el modelo ya los soporta:
 *  - Con OC (`poId` real): las líneas vienen de la orden de compra.
 *  - Sin OC (`poId` = "sin-oc"): las líneas se arman escaneando productos.
 *
 * Confirma con POST /api/supply-chain action=receipt, que es el camino completo
 * que impacta stock y admite purchase_order_id nulo. No agrega estados nuevos.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PickingShell } from "@/components/picking/PickingShell";
import { ScanField } from "@/components/picking/ScanField";
import { pickingJson } from "@/lib/picking/session";

export const Route = createFileRoute("/picking/recepciones/$poId")({
  component: RecepcionDetailPage,
  head: () => ({ meta: [{ title: "Recibir mercadería — Structura Picking" }] }),
});

const NO_PO = "sin-oc";

interface PurchaseOrder {
  id: string;
  supplierId: string;
  supplierName?: string;
  locationId: string | null;
  items: Array<{
    id: string;
    productId: string;
    productName: string;
    quantity: number;
    receivedQuantity: number;
    costPrice: number | null;
  }>;
}

interface ReceiptLine {
  key: string;
  poLineId: string | null;
  productId: string;
  descripcion: string;
  expected: number | null;
  qtyReceived: string;
  costoUnitario: number | null;
  unit: string;
}

function RecepcionDetailPage() {
  const { poId } = Route.useParams();
  const navigate = useNavigate();
  const withoutPo = poId === NO_PO;

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<ReceiptLine[]>([]);
  const [loading, setLoading] = useState(!withoutPo);
  const [saving, setSaving] = useState(false);
  const [numero, setNumero] = useState("");

  useEffect(() => {
    if (withoutPo) return;
    void (async () => {
      try {
        const data = await pickingJson<{ po: PurchaseOrder | null }>(
          `/api/purchase-orders?id=${encodeURIComponent(poId)}`,
        );
        if (!data.po) {
          toast.error("Orden de compra no encontrada");
          return;
        }
        setPo(data.po);
        setLines(
          data.po.items
            .filter((i) => i.receivedQuantity < i.quantity)
            .map((i) => ({
              key: i.id,
              poLineId: i.id,
              productId: i.productId,
              descripcion: i.productName,
              expected: i.quantity - i.receivedQuantity,
              qtyReceived: "",
              costoUnitario: i.costPrice,
              unit: "UNIDADES",
            })),
        );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "No se pudo cargar la orden");
      } finally {
        setLoading(false);
      }
    })();
  }, [poId, withoutPo]);

  /** Con OC el escaneo confirma la cantidad esperada; sin OC agrega la línea. */
  const handleCode = useCallback(
    async (code: string) => {
      let match: { productId: string; name: string; unidadMedida: string | null } | null = null;
      try {
        const data = await pickingJson<{
          match?: { productId: string; name: string; unidadMedida: string | null };
        }>(`/api/picking/lookup?code=${encodeURIComponent(code)}`);
        match = data.match ?? null;
      } catch {
        toast.error("No se encontró ningún producto con ese código");
        return;
      }
      if (!match) {
        toast.error("No se encontró ningún producto con ese código");
        return;
      }

      const existing = lines.find((l) => l.productId === match!.productId);
      if (existing) {
        setLines((prev) =>
          prev.map((l) =>
            l.key === existing.key
              ? {
                  ...l,
                  qtyReceived:
                    l.qtyReceived.trim() === ""
                      ? String(l.expected ?? 1)
                      : String(Number(l.qtyReceived.replace(",", ".") || 0) + 1),
                }
              : l,
          ),
        );
        toast.success(existing.descripcion);
        return;
      }

      if (!withoutPo) {
        toast.error("Ese producto no está en esta orden de compra");
        return;
      }

      setLines((prev) => [
        ...prev,
        {
          key: `${match!.productId}-${prev.length}`,
          poLineId: null,
          productId: match!.productId,
          descripcion: match!.name,
          expected: null,
          qtyReceived: "1",
          costoUnitario: null,
          unit: match!.unidadMedida?.toUpperCase() ?? "UNIDADES",
        },
      ]);
      toast.success(`${match.name} agregado`);
    },
    [lines, withoutPo],
  );

  const confirm = useCallback(async () => {
    const payloadLines = lines
      .map((l) => ({ ...l, qty: Number(l.qtyReceived.replace(",", ".")) }))
      .filter((l) => Number.isFinite(l.qty) && l.qty > 0);

    if (!payloadLines.length) {
      toast.error("Cargá al menos una cantidad recibida");
      return;
    }
    if (!numero.trim()) {
      toast.error("Ingresá el número de remito");
      return;
    }

    setSaving(true);
    try {
      await pickingJson("/api/supply-chain", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "receipt",
          numero: numero.trim(),
          purchaseOrderId: withoutPo ? null : poId,
          supplierId: po?.supplierId ?? null,
          locationId: po?.locationId ?? null,
          lines: payloadLines.map((l) => ({
            poLineId: l.poLineId,
            productId: l.productId,
            descripcion: l.descripcion,
            qtyReceived: l.qty,
            unit: l.unit,
            costoUnitario: l.costoUnitario ?? undefined,
          })),
        }),
      });
      toast.success("Recepción registrada");
      void navigate({ to: "/picking/recepciones" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo registrar la recepción");
    } finally {
      setSaving(false);
    }
  }, [lines, navigate, numero, po, poId, withoutPo]);

  return (
    <PickingShell
      title={withoutPo ? "Recepción sin OC" : (po?.supplierName ?? "Recepción")}
      backTo="/picking/recepciones"
    >
      {() =>
        loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="Número de remito"
              autoCapitalize="characters"
            />

            <ScanField
              onCode={(code) => void handleCode(code)}
              placeholder={
                withoutPo ? "Escaneá para agregar productos" : "Escaneá para confirmar cantidades"
              }
              autoFocus={false}
            />

            {lines.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {withoutPo
                  ? "Escaneá los productos que estás recibiendo."
                  : "Esta orden no tiene ítems pendientes de recibir."}
              </p>
            ) : (
              <div className="space-y-2">
                {lines.map((line) => (
                  <div
                    key={line.key}
                    className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {line.descripcion}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {line.expected != null ? `Esperado: ${line.expected}` : "Sin OC"}
                      </p>
                    </div>
                    <Input
                      value={line.qtyReceived}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === line.key ? { ...l, qtyReceived: e.target.value } : l,
                          ),
                        )
                      }
                      placeholder="0"
                      inputMode="decimal"
                      className="h-9 w-20 shrink-0 text-center"
                      aria-label={`Cantidad recibida de ${line.descripcion}`}
                    />
                    {withoutPo ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-9 w-9 shrink-0"
                        onClick={() =>
                          setLines((prev) => prev.filter((l) => l.key !== line.key))
                        }
                        aria-label={`Quitar ${line.descripcion}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              disabled={saving || lines.length === 0}
              onClick={() => void confirm()}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Registrar recepción
            </Button>
          </div>
        )
      }
    </PickingShell>
  );
}
